import { useState, useEffect } from "react";
import { cn } from "@/dentlab/src/lib/util";
import { INVALID_CONTROL_CLASS } from "@/dentlab/src/components/input-wrapper";

// The options cover the group's background, so they carry the error tint themselves.
const INVALID_OPTION_CLASS = "bg-red-100 hover:bg-red-200";

interface CustomToggleButtonGroupOption {
	value: number | null;
	label: string;
	disabled?: boolean;
}

interface CustomToggleButtonGroupProps {
	value: number | null | undefined;
	onChange: (value: number | null) => void;
	options: CustomToggleButtonGroupOption[];
	isInput?: boolean;
	inputHeight?: number;
	inputPlaceholder?: string;
	ariaLabel?: string;
	testId?: string;
	invalid?: boolean;
}

export const CustomToggleButtonGroup: React.FC<
	CustomToggleButtonGroupProps
> = ({
	value,
	onChange,
	options,
	isInput = false,
	inputHeight = 48,
	inputPlaceholder = "TP",
	ariaLabel,
	testId,
	invalid = false,
}) => {
	const isCustom =
		isInput && value != null && !options.some((o) => o.value === value);

	const [customInput, setCustomInput] = useState<string>(
		isCustom ? String(value) : ""
	);

	useEffect(() => {
		if (isCustom) setCustomInput(String(value));
	}, [value]);

	const totalCols = options.length + (isInput ? 1 : 0);

	const handleCustomClick = () => {
		if (isCustom || !customInput) return;
		const num = parseFloat(customInput);
		if (isNaN(num) || num <= 0) return;
		onChange(num);
	};

	const handleCustomChange = (raw: string) => {
		const v = raw.replace(",", ".");
		if (v !== "" && !/^-?\d*\.?\d*$/.test(v)) return;
		setCustomInput(v);
		const num = parseFloat(v);
		if (!isNaN(num) && num > 0) onChange(num);
	};

	return (
		<div
			className={cn(
				"grid border rounded-md overflow-hidden",
				invalid && INVALID_CONTROL_CLASS
			)}
			style={{
				gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`,
				height: `${inputHeight}px`,
			}}
			role="group"
			aria-label={ariaLabel}
			aria-invalid={invalid}
			data-testid={testId}
		>
			{options.map((opt, idx) => {
				const isActive = value === opt.value;
				const isLast = idx === totalCols - 1;
				return (
					<button
						key={`${idx}-${String(opt.value)}`}
						type="button"
						disabled={opt.disabled}
						aria-pressed={isActive}
						aria-label={opt.label}
						data-testid={
							testId ? `${testId}-option-${idx}` : undefined
						}
						className={cn(
							"flex items-center justify-center transition-colors font-medium px-2 text-sm",
							!isLast && "border-r",
							isActive
								? "bg-primary text-primary-foreground"
								: invalid
									? INVALID_OPTION_CLASS
									: "bg-background hover:bg-muted",
							opt.disabled &&
								"opacity-50 cursor-not-allowed hover:bg-background"
						)}
						onClick={() => onChange(opt.value)}
					>
						{opt.label}
					</button>
				);
			})}
			{isInput && (
				<input
					type="text"
					className={cn(
						"text-center outline-none min-w-0 cursor-pointer text-sm",
						isCustom
							? "bg-primary text-primary-foreground font-medium"
							: cn(
									"text-muted-foreground",
									invalid
										? INVALID_OPTION_CLASS
										: "bg-background hover:bg-muted"
								)
					)}
					placeholder={inputPlaceholder}
					value={customInput}
					aria-label={inputPlaceholder}
					data-testid={testId ? `${testId}-custom` : undefined}
					onClick={handleCustomClick}
					onChange={(e) => handleCustomChange(e.target.value)}
				/>
			)}
		</div>
	);
};

export default CustomToggleButtonGroup;
